import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { useLanguage } from "./LanguageContext";

// Icons
const PlayIcon = ({ className }: { className?: string }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="5,3 19,12 5,21"></polygon>
  </svg>
);

const ClockIcon = ({ className }: { className?: string }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12,6 12,12 16,14"></polyline>
  </svg>
);

const BookOpenIcon = ({ className }: { className?: string }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
  </svg>
);

const AwardIcon = ({ className }: { className?: string }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="7"></circle>
    <polyline points="8.21,13.89 7,23 12,20 17,23 15.79,13.88"></polyline>
  </svg>
);

export function Academy() {
  const { getBilingualText } = useLanguage();

  const courses = [
    {
      title: String(getBilingualText("academy.course1.title")),
      description: String(getBilingualText("academy.course1.description")),
      duration: "2 horas",
      level: String(getBilingualText("academy.courses.level.beginner")),
      status: String(getBilingualText("academy.courses.status.available")),
      progress: 0
    },
    {
      title: String(getBilingualText("academy.course2.title")),
      description: String(getBilingualText("academy.course2.description")),
      duration: "3 horas",
      level: String(getBilingualText("academy.courses.level.intermediate")),
      status: String(getBilingualText("academy.courses.status.inProgress")),
      progress: 65
    },
    {
      title: String(getBilingualText("academy.course3.title")),
      description: String(getBilingualText("academy.course3.description")),
      duration: "2.5 horas",
      level: String(getBilingualText("academy.courses.level.advanced")),
      status: String(getBilingualText("academy.courses.status.available")),
      progress: 0
    },
    {
      title: String(getBilingualText("academy.course4.title")),
      description: String(getBilingualText("academy.course4.description")),
      duration: "4 horas",
      level: String(getBilingualText("academy.courses.level.advanced")),
      status: String(getBilingualText("academy.courses.status.comingSoon")),
      progress: 0
    }
  ];

  const webinars = [
    {
      title: String(getBilingualText("academy.webinar1.title")),
      date: "15 Enero 2025",
      time: "16:00 CET",
      speaker: String(getBilingualText("academy.webinar1.speaker")),
      type: "Live"
    },
    {
      title: String(getBilingualText("academy.webinar2.title")),
      date: "22 Enero 2025", 
      time: "17:00 CET",
      speaker: String(getBilingualText("academy.webinar2.speaker")),
      type: "Live"
    }
  ];

  const getLevelColor = (level: string) => {
    const beginnerLabel = String(getBilingualText("academy.courses.level.beginner"));
    const intermediateLabel = String(getBilingualText("academy.courses.level.intermediate"));
    const advancedLabel = String(getBilingualText("academy.courses.level.advanced"));
    
    switch (level) {
      case beginnerLabel:
        return "bg-green-100 text-green-800";
      case intermediateLabel:
        return "bg-yellow-100 text-yellow-800";
      case advancedLabel:
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusColor = (status: string) => {
    const availableLabel = String(getBilingualText("academy.courses.status.available"));
    const inProgressLabel = String(getBilingualText("academy.courses.status.inProgress"));
    const comingSoonLabel = String(getBilingualText("academy.courses.status.comingSoon"));
    
    switch (status) {
      case availableLabel:
        return "bg-blue-100 text-blue-800";
      case inProgressLabel:
        return "bg-green-100 text-green-800";
      case comingSoonLabel:
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="space-y-1">
        <h1 className="text-2xl text-gray-900" style={{ fontWeight: 500 }}>{getBilingualText("academy.title")}</h1>
        <p className="text-sm text-gray-500">{getBilingualText("academy.subtitle")}</p>
      </div>

      {/* Featured Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card className="bg-gradient-to-r from-blue-600 to-blue-700 text-white border-0">
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <AwardIcon />
                  <span className="text-sm font-medium">{getBilingualText("academy.featured.badge")}</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold">{getBilingualText("academy.featured.title")}</h3>
                  <p className="text-blue-100 text-sm mt-2">
                    {getBilingualText("academy.featured.description")}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1">
                    <ClockIcon />
                    <span className="text-sm">3.5 horas</span>
                  </div>
                  <Badge className="bg-white/20 text-white border-white/20">{getBilingualText("academy.courses.level.intermediate")}</Badge>
                </div>
                <Button className="bg-white text-blue-600 hover:bg-gray-100">
                  <PlayIcon className="mr-2" />
                  {getBilingualText("academy.featured.start")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900">{getBilingualText("academy.progress")}</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{getBilingualText("academy.progress.completed")}</span>
                    <span className="font-medium">3/8</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full" style={{ width: '37.5%' }}></div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{getBilingualText("academy.progress.certifications")}</span>
                    <span className="font-medium">2</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>{getBilingualText("academy.progress.studyHours")}</span>
                    <span className="font-medium">12.5h</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900">{getBilingualText("academy.nextWebinar.title")}</h4>
                <div className="space-y-2">
                  <p className="text-sm font-medium">{getBilingualText("academy.webinar1.title")}</p>
                  <div className="space-y-1">
                    <p className="text-xs text-gray-500">15 Enero 2025 • 16:00 CET</p>
                    <p className="text-xs text-gray-500">{getBilingualText("academy.webinar1.speaker")}</p>
                  </div>
                  <Button size="sm" className="w-full bg-blue-600 hover:bg-blue-700">
                    {getBilingualText("academy.nextWebinar.join")}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Courses Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">{getBilingualText("academy.courses.available")}</h2>
          <Button variant="outline" size="sm">{getBilingualText("academy.courses.viewAll")}</Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {courses.map((course, index) => (
            <Card key={index} className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-1 flex-1">
                    <CardTitle className="text-base font-medium">{course.title}</CardTitle>
                    <CardDescription className="text-sm text-gray-600">
                      {course.description}
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <Badge className={getLevelColor(course.level)} variant="secondary">
                    {course.level}
                  </Badge>
                  <Badge className={getStatusColor(course.status)} variant="secondary">
                    {course.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-3">
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <ClockIcon />
                      <span>{course.duration}</span>
                    </div>
                    {course.status === getBilingualText("academy.courses.status.inProgress") && (
                      <span>{course.progress}% {getBilingualText("academy.courses.completed")}</span>
                    )}
                  </div>
                  
                  {course.status === getBilingualText("academy.courses.status.inProgress") && (
                    <div className="space-y-1">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-600 h-2 rounded-full transition-all" 
                          style={{ width: `${course.progress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                  
                  <Button 
                    className="w-full" 
                    variant={course.status === getBilingualText("academy.courses.status.comingSoon") ? "outline" : "default"}
                    disabled={course.status === getBilingualText("academy.courses.status.comingSoon")}
                  >
                    {course.status === getBilingualText("academy.courses.status.inProgress") ? (
                      <>
                        <PlayIcon className="mr-2" />
                        {getBilingualText("academy.courses.continue")}
                      </>
                    ) : course.status === getBilingualText("academy.courses.status.comingSoon") ? (
                      getBilingualText("academy.courses.status.comingSoon")
                    ) : (
                      <>
                        <BookOpenIcon className="mr-2" />
                        {getBilingualText("academy.courses.start")}
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Webinars Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">{getBilingualText("academy.webinars.upcoming")}</h2>
          <Button variant="outline" size="sm">{getBilingualText("academy.webinars.viewCalendar")}</Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {webinars.map((webinar, index) => (
            <Card key={index} className="bg-white border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <h4 className="font-medium text-gray-900">{webinar.title}</h4>
                      <p className="text-sm text-gray-600">{webinar.speaker}</p>
                    </div>
                    <Badge className="bg-red-100 text-red-800">{getBilingualText("academy.webinars.live")}</Badge>
                  </div>
                  <div className="text-sm text-gray-500">
                    <p>{webinar.date} • {webinar.time}</p>
                  </div>
                  <Button size="sm" className="w-full bg-blue-600 hover:bg-blue-700">
                    {getBilingualText("academy.webinars.register")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}